import { useAuth } from "@/hooks/use-auth";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Check, Gift, Trophy, Zap, ArrowUpRightFromCircle, Sparkles, Shield, Users, Heart, ArrowUp } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "wouter";
import { EnhancedHeader } from "@/components/ui/enhanced-header";

// Define release types for proper categorization
type ReleaseType = "major" | "feature" | "improvement" | "bugfix";

// Define the structure for a release entry
interface ReleaseEntry {
  version: string;
  date: string;
  type: ReleaseType;
  highlights: string[];
  details: string[];
}

// Organize the releases by version
const releases: ReleaseEntry[] = [
  {
    version: "1.3.1",
    date: "May 25 2025",
    type: "feature",
    highlights: ["Awards"],
    details: [
      "Added awards system to award players for achievements",
      "Tournament page now allows for watching replays",
      "Fixed spectator count not showing in games",
      "Removed display of card count from games",
    ]
  },
  {
    version: "1.2.5",
    date: "May 12 2025",
    type: "feature",
    highlights: ["Gamplay", "Scoring"],
    details: [
      "Improved scoring system to account for more scenarios",
      "Improved gameplay sequence"
    ]
  },
  {
    version: "1.2.4",
    date: "Apr 30 2025",
    type: "feature",
    highlights: ["Push Notifications", "Offline Mode Improvements"],
    details: [
      "Added push notification support for mobile devices (iOS and Android)",
      "Enhanced offline mode with improved reconnection handling",
      "Added achievement notifications with custom animations",
      "Improved mobile audio system with better background audio handling",
      "Enhanced friend status updates with real-time online indicators"
    ]
  },
  {
    version: "1.2.3",
    date: "Mar 28 2025",
    type: "feature",
    highlights: ["Dual currency system", "UI improvements"],
    details: [
      "Added dual currency system for card decks (regular Coins and premium Gems)",
      "Improved navigation with dedicated About page and streamlined homepage tabs",
      "Enhanced mobile responsiveness across the application",
      "Updated deck marketplace to display appropriate currency type icons"
    ]
  },
  {
    version: "1.2.2",
    date: "Mar 25 2025",
    type: "feature",
    highlights: ["Tournament mode"],
    details: [
      "Added tournament mode. Users can now create a tournament"
    ]
  },
  {
    version: "1.2.1",
    date: "Mar 23 2025",
    type: "improvement",
    highlights: ["Achievements tracking", "Improved matchmaking"],
    details: [
      "Added achievements tracking.",
      "Improved matchmaking.",
      "Matchmaking now has a 30 second accept reject countdown",
      "Improvements to games cleanup and points consolidation"
    ]
  },
  {
    version: "1.2.0",
    date: "Mar 13 2025",
    type: "feature",
    highlights: ["Deck marketplace", "Redesigned games page"],
    details: [
      "Redesigned games page.",
      "Added initial marketplace for acquiring deck designs.",
      "Playing a card is now tap twice to play"
    ]
  },
  {
    version: "1.1.0",
    date: "Mar 01 2025",
    type: "feature",
    highlights: ["Practice mode"],
    details: [
      "Added practice mode for the game. Players can now play against computer."
    ]
  },
  {
    version: "1.0.7",
    date: "Feb 20 2025",
    type: "feature",
    highlights: ["Ranked Matchmaking", "Match history"],
    details: [
      "Added Ranked Matchmaking: players can now search for a match and be paired with other players who are searching.",
      "Added starting a game from friends page",
      "Match ends now after a player reaches a score of 10",
      "Added profile page improvements with match history",
      "A deck is now played in full before shuffling a new one",
      "Added how to play information on homepage"
    ]
  },
  {
    version: "1.0.6",
    date: "Feb 15 2025",
    type: "improvement",
    highlights: ["Connection stability", "Session management"],
    details: [
      "Improved connection quality",
      "Added better clean up for joining and leaving games",
      "Improved spectator count for connect and disconnects",
      "Game room now cleans up after 10 minutes of inactivity"
    ]
  },
  {
    version: "1.0.5",
    date: "Feb 14 2025",
    type: "bugfix",
    highlights: ["Game stability fixes", "UI improvements"],
    details: [
      "Fixed no activity when players deal or play card after period of inactivity. Game automatically restarts with the deck and hand before disconnecting. (Points and round lost, will be fixed in future updates)",
      "Added filter to watch pages to show only started matches. Match starts after first dealing of cards",
      "Fixed friends only games not showing in games page",
      "Changed to looser of previous round playing first for the next round",
      "Added gameplay sounds",
      "Added a banner to show round winner"
    ]
  },
  {
    version: "1.0.4",
    date: "Feb 13 2025",
    type: "improvement",
    highlights: ["Card dealing improvements", "Game history"],
    details: [
      "Fixed card dealing now working correctly. Cards now dealt according to share structure of 3-3-2-2",
      "Player to start next round now defaults to winner of last round",
      "Added game ID to game listing so players can manually join a game using the game id with the join game icon at top right of games page",
      "Improved game server ping and stabilized connections",
      "Changed layout of game listing on game and watch pages",
      "Added watch count on game listing watch page",
      "Started saving game history and storing gameplay sequence",
      "Added new card shuffle sounds",
      "Added more soundtracks to play at random"
    ]
  },
  {
    version: "1.0.3",
    date: "Feb 12 2025",
    type: "bugfix",
    highlights: ["Spectator mode fixes", "Audio improvements"],
    details: [
      "Fixed spectator mode giving a blank page when second player hasn't joined game",
      "Randomized audio playlist with a shuffle"
    ]
  },
  {
    version: "1.0.2",
    date: "Feb 11 2025",
    type: "feature",
    highlights: ["Spectator Mode"],
    details: [
      "Added Spectator Mode"
    ]
  },
  {
    version: "1.0.1",
    date: "Feb 8 2025",
    type: "bugfix",
    highlights: ["Game fixes", "Friends system"],
    details: [
      "Added fixes to game logc",
      "Added friends list and ability to add a friend",
      "Fixed delay in player pairing to start match"
    ]
  },
  {
    version: "1.0.0",
    date: "Feb 4 2025",
    type: "major",
    highlights: ["Initial Release", "1v1 Gameplay"],
    details: [
      "Initial launch with basic 1v1 gameplay",
      "Added profile page"
    ]
  }
];

// Helper function to get icon for release type
const getReleaseIcon = (type: ReleaseType) => {
  switch (type) {
    case "major":
      return <Trophy className="h-5 w-5 text-yellow-400" />;
    case "feature":
      return <Gift className="h-5 w-5 text-blue-400" />;
    case "improvement":
      return <Zap className="h-5 w-5 text-purple-400" />;
    case "bugfix":
      return <Check className="h-5 w-5 text-green-400" />;
    default:
      return <Check className="h-5 w-5" />;
  }
};

// Helper function to get badge color for release type
const getReleaseBadgeClass = (type: ReleaseType) => {
  switch (type) {
    case "major":
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
    case "feature":
      return "bg-blue-500/20 text-blue-400 border-blue-500/50";
    case "improvement":
      return "bg-purple-500/20 text-purple-400 border-purple-500/50";
    case "bugfix":
      return "bg-green-500/20 text-green-400 border-green-500/50";
    default:
      return "bg-primary/20 text-primary border-primary/50";
  }
};

// Animation variants for framer-motion
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.05,
      duration: 0.4,
      ease: "easeOut"
    }
  })
};

export default function ChangeLog() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [displayedReleases, setDisplayedReleases] = useState<ReleaseEntry[]>(releases);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...releases];
    
    // Apply category filter
    if (filter !== "all") {
      filtered = filtered.filter(release => release.type === filter);
    }
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(release => 
        release.version.toLowerCase().includes(term) ||
        release.date.toLowerCase().includes(term) ||
        release.highlights.some(h => h.toLowerCase().includes(term)) ||
        release.details.some(d => d.toLowerCase().includes(term))
      );
    }
    
    setDisplayedReleases(filtered);
  }, [filter, searchTerm]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black pb-16 sm:pb-20">
       {/* Enhanced header */}
       <EnhancedHeader
          title="Change Log"
          backPath="/user-settings"
          coloredText={true}
          rightContent={null}
        />
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 pb-16 sm:pb-20">
       
        
        {/* Badge legend - hidden on mobile, shown on tablet+ */}
        <div className="hidden md:flex gap-2 my-6 justify-end">
          <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/50 py-1.5">
            <Trophy className="h-4 w-4 mr-1" /> Major
          </Badge>
          <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/50 py-1.5">
            <Gift className="h-4 w-4 mr-1" /> Feature
          </Badge>
          <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/50 py-1.5">
            <Zap className="h-4 w-4 mr-1" /> Improvement
          </Badge>
          <Badge className="bg-green-500/20 text-green-400 border border-green-500/50 py-1.5">
            <Check className="h-4 w-4 mr-1" /> Bugfix
          </Badge>
        </div>
        
        {/* Mobile badge legend - simplified, vertical stack */}
        <div className="flex md:hidden flex-wrap justify-center gap-2 my-6">
          <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/50 py-1">
            <Trophy className="h-3 w-3 mr-1" /> Major
          </Badge>
          <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/50 py-1">
            <Gift className="h-3 w-3 mr-1" /> Feature
          </Badge>
          <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/50 py-1">
            <Zap className="h-3 w-3 mr-1" /> Improve
          </Badge>
          <Badge className="bg-green-500/20 text-green-400 border border-green-500/50 py-1">
            <Check className="h-3 w-3 mr-1" /> Fix
          </Badge>
        </div>
        
        {/* Responsive tabs */}
        <div className="mb-8">
          <Tabs defaultValue="all" onValueChange={(value) => setFilter(value)}>
            <TabsList className="grid grid-cols-3 md:grid-cols-5 w-full max-w-2xl mx-auto">
              <TabsTrigger value="all" className="text-sm md:text-base">All</TabsTrigger>
              <TabsTrigger value="major" className="text-sm md:text-base">Major</TabsTrigger>
              <TabsTrigger value="feature" className="text-sm md:text-base">Features</TabsTrigger>
              <TabsTrigger value="improvement" className="text-sm md:text-base hidden md:inline-flex">Improvements</TabsTrigger>
              <TabsTrigger value="improvement" className="text-sm md:text-base md:hidden">Improve</TabsTrigger>
              <TabsTrigger value="bugfix" className="text-sm md:text-base hidden md:inline-flex">Bug Fixes</TabsTrigger>
              <TabsTrigger value="bugfix" className="text-sm md:text-base md:hidden">Fixes</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        <div className="grid grid-cols-1 gap-8">
          {displayedReleases.length === 0 ? (
            <div className="text-center p-8 bg-black/30 rounded-lg border border-gray-800">
              <p className="text-gray-400">No releases match your filter criteria.</p>
            </div>
          ) : (
            displayedReleases.map((release, index) => (
              <motion.div
                key={release.version}
                custom={index}
                initial="hidden"
                animate="visible"
                variants={cardVariants}
              >
                <Card className="bg-black/40 border border-gray-800 backdrop-blur-md overflow-hidden">
                  <div className={`h-1 w-full ${release.type === 'major' ? 'bg-yellow-500' : release.type === 'feature' ? 'bg-blue-500' : release.type === 'improvement' ? 'bg-purple-500' : 'bg-green-500'}`} />
                  <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 gap-3">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full p-2 bg-black/30 border border-gray-800">
                        {getReleaseIcon(release.type)}
                      </div>
                      <div>
                        <CardTitle className="text-xl sm:text-2xl font-bold flex flex-wrap items-center gap-2">
                          Version {release.version}
                          {release.version === "1.2.3" && (
                            <Badge className="bg-primary/20 text-primary border border-primary/50">
                              Latest
                            </Badge>
                          )}
                        </CardTitle>
                        <div className="flex items-center text-gray-400 mt-1 text-sm">
                          <Calendar className="h-3.5 w-3.5 mr-1.5" />
                          <span>{release.date}</span>
                        </div>
                      </div>
                    </div>
                    <Badge className={`${getReleaseBadgeClass(release.type)} border px-3 py-1 text-xs sm:text-sm self-start sm:self-center`}>
                      {release.type.charAt(0).toUpperCase() + release.type.slice(1)}
                    </Badge>
                  </CardHeader>
                  
                  <CardContent className="pt-3 sm:pt-4">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {release.highlights.map((highlight, idx) => (
                        <Badge key={idx} variant="outline" className="bg-black/30 text-white border-gray-700 py-1 sm:py-1.5 px-2 sm:px-3 text-xs sm:text-sm">
                          <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5 text-primary" />
                          {highlight}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="space-y-2 pl-2 border-l-2 border-gray-800">
                      {release.details.map((detail, idx) => (
                        <div key={idx} className="flex items-start gap-2 sm:gap-3 group">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 group-hover:scale-125 transition-transform" />
                          <p className="text-gray-300 flex-1 text-sm sm:text-base">{detail}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  
                  {(['1.0.0', '1.1.0', '1.2.0', '1.2.3'].includes(release.version)) && (
                    <CardFooter className="pb-4 sm:pb-6 pt-1 sm:pt-2">
                      <div className="w-full">
                        <div className="p-2 sm:p-3 rounded-md bg-primary/10 border border-primary/20 flex items-start sm:items-center flex-col sm:flex-row">
                          {release.version === '1.0.0' && (
                            <>
                              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 sm:mr-3 mb-1 sm:mb-0" />
                              <p className="text-xs sm:text-sm text-gray-300">First stable release of Spar Legends with core gameplay features</p>
                            </>
                          )}
                          {release.version === '1.1.0' && (
                            <>
                              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 sm:mr-3 mb-1 sm:mb-0" />
                              <p className="text-xs sm:text-sm text-gray-300">Major update introducing AI opponents for practice games</p>
                            </>
                          )}
                          {release.version === '1.2.0' && (
                            <>
                              <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 sm:mr-3 mb-1 sm:mb-0" />
                              <p className="text-xs sm:text-sm text-gray-300">Visual refresh with new deck marketplace and improved card interactions</p>
                            </>
                          )}
                          {release.version === '1.2.3' && (
                            <>
                              <Gift className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 sm:mr-3 mb-1 sm:mb-0" />
                              <p className="text-xs sm:text-sm text-gray-300">Added support for dual currency (Coins and Gems) with enhanced mobile experience</p>
                            </>
                          )}
                        </div>
                      </div>
                    </CardFooter>
                  )}
                </Card>
              </motion.div>
            ))
          )}
        </div>
        
        {/* Back to top button - responsive */}
        <div className="fixed bottom-4 sm:bottom-8 right-4 sm:right-8 z-50">
          <Button
            variant="outline"
            size="icon"
            className="rounded-full bg-primary/90 text-white hover:bg-primary/80 shadow-md h-9 w-9 sm:h-10 sm:w-10"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <ArrowUp className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
